import React, { useState, useEffect, useRef, useMemo } from "react";
import icons from "../../assets/images/icons";
import styles from "./Select.module.scss";
import { HorizontalFlex } from "../flex/HorizontalFlex";
import { BottomSheet } from "../material/Popup";
import { VerticalFlex } from "../flex/VerticalFlex";
import { useSelectHook } from "./useSelectHook";
import PropTypes from "prop-types";
import RangeInput from "../input/RangeInput";
import { DefaultInputText, MinMaxInput } from "../input/InputText";
import { Range, getTrackBackground } from "react-range";
import { debounce } from "lodash";
import { IconButton } from "../buttons/IconButton";
import buttonStyle from "../buttons/buttons.module.scss";
import { usePopoverScrollTracking } from "../../utils/popoverPosition";
import { DefaultResetButton } from "../buttons/ResetButton";

const REGION_STRUCTURE = {
  경기도: {
    수영시: ["수영음", "건사음"],
    백화군: ["건열음"],
  },
  서울시: {
    강남구: ["논현동", "도곡동"],
    성동구: ["화계동"],
  },
};

const getDefaultLocation = () => {
  // 현재 지도의 위치에서 기본적으로 선택된 경우의 상황 문제 간단히 mock 해서 보입니다.
  return {
    province: "서울시",
    city: "강남구",
    district: "논현동",
  };
};

export function RegionSelector({ onConfirm }) {
  const [selected, setSelected] = useState(getDefaultLocation());
  const [provinces] = useState(Object.keys(REGION_STRUCTURE));
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);

  useEffect(() => {
    const province = selected.province;
    if (province) {
      const cityList = Object.keys(REGION_STRUCTURE[province] || {});
      setCities(cityList);
      setSelected((prev) => ({ ...prev, city: cityList[0], district: null }));
    }
  }, [selected.province]);

  useEffect(() => {
    const { province, city } = selected;
    if (province && city) {
      const districtList = REGION_STRUCTURE[province][city] || [];
      setDistricts(districtList);
      setSelected((prev) => ({ ...prev, district: districtList[0] }));
    }
  }, [selected.city]);

  const handleChange = (level, value) => {
    setSelected((prev) => ({ ...prev, [level]: value }));
  };

  const handleConfirm = () => {
    onConfirm(selected);
  };

  return (
    <div className="p-4 bg-white shadow-md rounded-xl w-full max-w-md">
      <h2 className="text-lg font-bold mb-4">경도 및 동 선택</h2>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div>
          {provinces.map((p) => (
            <div
              key={p}
              className={`py-2 cursor-pointer ${selected.province === p ? "text-blue-600 font-semibold" : "text-gray-600"}`}
              onClick={() => handleChange("province", p)}
            >
              {p}
            </div>
          ))}
        </div>
        <div>
          {cities.map((c) => (
            <div
              key={c}
              className={`py-2 cursor-pointer ${selected.city === c ? "text-blue-600 font-semibold" : "text-gray-600"}`}
              onClick={() => handleChange("city", c)}
            >
              {c}
            </div>
          ))}
        </div>
        <div>
          {districts.map((d) => (
            <div
              key={d}
              className={`py-2 cursor-pointer ${selected.district === d ? "text-blue-600 font-semibold" : "text-gray-600"}`}
              onClick={() => handleChange("district", d)}
            >
              {d}
            </div>
          ))}
        </div>
      </div>
      <button onClick={handleConfirm} className="mt-4 w-full">
        {selected.province} {selected.city} {selected.district} 으로 이동
      </button>
    </div>
  );
}

const DefaultSelectButton = ({
  className = "",
  isSelected = "",
  placeholder = "선택",
  onClick = () => {},
}) => {
  return (
    <div
      className={`${styles.defaultSelectButton} ${isSelected ? styles.selected : ""} ${className}`}
      onClick={onClick}
    >
      <span>{placeholder}</span>
      <img src={isSelected ? icons.arrow.down.blue : icons.arrow.down.black} />
    </div>
  );
};

/**
 * defaultfilterButton 컴포넌트
 *
 * 기본 선택 버튼을 렌더링합니다.
 *
 * @param {string} className - 추가 CSS 클래스 이름
 * @param {boolean} isSelected - 선택 여부
 * @param {string} placeholder - 선택 전 표시할 기본 텍스트
 * @param {function} onClick - 클릭 시 호출되는 콜백 함수
 */
const DefaultfilterButton = ({
  isOpen = false,
  className = "",
  isSelected = "",
  placeholder = "선택",
  onClick = () => {},
}) => {
  return (
    <div
      className={`${styles.defaultfilterButton} ${isSelected ? styles.selected : ""} ${className}`}
      onClick={onClick}
    >
      <span>{placeholder}</span>
      <img
        src={isOpen ? icons.arrow.up.filter_black : icons.arrow.down.filter_black}
        width={16}
        height={16}
      />
    </div>
  );
};

/**
 * DropdownSelect 컴포넌트
 *
 * 드롭다운 형태의 선택 UI를 렌더링합니다.
 *
 */

const DropdownSelect = ({
  className = "",
  placeholder = "선택",
  options = [],
  selected = [],
  multiple = false,
  onChange = () => {},
  labelSeperator = ",",
}) => {
  const {
    isOpen,
    setIsOpen,
    togglePopover,
    selectedValues,
    selectAll,
    handleSelect,
    handleSelectAll,
    popoverRef,
  } = useSelectHook({
    selected: selected,
    options,
    multiple,
    onChange,
  });

  // 🎯 스크롤 추적을 위한 ref들
  const triggerRef = useRef(null);
  const popoverContentRef = useRef(null);

  // 🎯 팝오버 스크롤 추적 훅 사용
  usePopoverScrollTracking(isOpen, triggerRef, popoverContentRef, true);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  const [label, setLabel] = useState(placeholder);

  useEffect(() => {
    if (selectedValues != null && selectedValues.length > 0) {
      const selectedLabels = options
        .filter((option) => selectedValues.includes(option.value))
        .map((option) => option.label)
        .join(`${labelSeperator} `);
      setLabel(selectedLabels);
    } else {
      setLabel(placeholder);
    }
  }, [selectedValues]);

  return (
    <div ref={popoverRef} className={`${styles.withPopover} ${className}`}>
      <div ref={triggerRef}>
        <DefaultSelectButton placeholder={label} onClick={togglePopover} />
      </div>
      {isOpen && (
        <div ref={popoverContentRef} className={`${styles.dropdownContainer} ${styles.popover}`}>
          {options.map((option, index) => (
            <button
              key={index}
              className={`${styles.item} ${selectedValues != null && selectedValues.includes(option.value) ? styles.selected : ""}`}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

DropdownSelect.propTypes = {
  /** 추가 CSS 클래스 이름 */
  className: PropTypes.string,
  /** 선택 전 표시할 기본 텍스트 */
  placeholder: PropTypes.string,
  /** 선택 옵션 배열 ({ label, value } 형식) */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      /** 옵션의 표시 텍스트 */
      label: PropTypes.string.isRequired,
      /** 옵션의 값 */
      value: PropTypes.any.isRequired,
    })
  ),
  /** 선택된 값 배열 */
  selected: PropTypes.array,
  /** 다중 선택 여부 */
  multiple: PropTypes.bool,
  /** 선택값 변경 시 호출되는 콜백 함수 */
  onChange: PropTypes.func,
  /** 선택된 옵션들을 구분할 구분자 */
  labelSeperator: PropTypes.string,
};

/**
 * PopoverChipSelect 컴포넌트
 *
 * 팝오버 형태의 칩 선택 UI를 렌더링합니다.
 *
 * @param {string} className - 추가 CSS 클래스 이름
 * @param {string} placeholder - 선택 전 표시할 기본 텍스트
 * @param {string} label - 선택창 상단에 표시할 라벨
 * @param {Array<{label: string, value: any}>} options - 선택 옵션 배열
 * @param {Array<any>} selected - 선택된 값 배열
 * @param {boolean} multiple - 다중 선택 여부
 * @param {function} onChange - 선택값 변경 시 호출되는 콜백 함수
 * @param {boolean} showAllOption - 전체 선택 옵션 표시 여부
 * @param {string} allOptionLabel - 전체 선택 옵션의 라벨
 * @returns {JSX.Element} - 팝오버 칩 선택 컴포넌트
 */
const PopoverChipSelect = ({
  className = "",
  defaultSelectClassName = "",
  placeholder = "선택",
  label = "",
  options = [],
  selected = [],
  multiple = false,
  onChange = () => {},
  showAllOption = false,
  allOptionLabel = "전체",
  isGroup = false,
  groups = [],
}) => {
  const {
    isOpen,
    setIsOpen,
    togglePopover,
    selectedValues,
    selectAll,
    handleSelect,
    handleSelectAll,
    popoverRef,
  } = useSelectHook({
    selected: selected,
    options,
    multiple,
    onChange,
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div ref={popoverRef} className={`${styles.withPopover} ${className}`}>
      <DefaultSelectButton
        isSelected={selectedValues != null && selectedValues.length > 0}
        placeholder={placeholder}
        onClick={togglePopover}
        className={defaultSelectClassName}
      />
      {isOpen && (
        <div className={`${styles.popoverChipContainer} ${styles.popover}`}>
          <HorizontalFlex
            className={`${styles.header}`}
            leftChildren={
              <>
                <div className={`${styles.label}`}>{label}</div>
                {multiple && <div className={`${styles.isMultipleLabel}`}>중복선택가능</div>}
              </>
            }
            rightChildren={
              <div
                className={`${styles.close}`}
                onClick={() => {
                  setIsOpen(false);
                }}
              >
                <img src={icons.close.black} width={17} height={17} />
              </div>
            }
          />
          <div className={`${styles.body}`}>
            {multiple && showAllOption && (
              <button
                className={`${styles.chip} ${selectAll ? styles.selected : ""}`}
                onClick={() => {
                  if (!selectAll) {
                    handleSelectAll();
                  }
                }}
              >
                {allOptionLabel}
              </button>
            )}

            {isGroup
              ? groups.map((group, gIdx) => (
                  <div key={gIdx} className={styles.groupSection}>
                    <div className={styles.groupLabel}>{group.label}</div>
                    <div className={styles.optionWrapper}>
                      {group.options.map((option, index) => (
                        <button
                          key={index}
                          className={`${styles.chip} ${selectedValues != null && selectedValues.includes(option.value) && !selectAll ? styles.selected : ""}`}
                          onClick={() => handleSelect(option.value)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              : options.map((option, index) => (
                  <button
                    key={index}
                    className={`${styles.chip} ${selectedValues != null && selectedValues.includes(option.value) && !selectAll ? styles.selected : ""}`}
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
          </div>
        </div>
      )}
    </div>
  );
};

PopoverChipSelect.propTypes = {
  /** 추가 CSS 클래스 이름 */
  className: PropTypes.string,
  /** 선택 전 표시할 기본 텍스트 */
  placeholder: PropTypes.string,
  /** 선택창 상단에 표시할 라벨 */
  label: PropTypes.string,
  /** 선택 옵션 배열 ({ label, value } 형식) */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      /** 옵션의 표시 텍스트 */
      label: PropTypes.string.isRequired,
      /** 옵션의 값 */
      value: PropTypes.any.isRequired,
    })
  ),
  /** 선택된 값 배열 */
  selected: PropTypes.array,
  /** 다중 선택 여부 */
  multiple: PropTypes.bool,
  /** 선택값 변경 시 호출되는 콜백 함수 */
  onChange: PropTypes.func,
  /** 전체 선택 옵션 표시 여부 */
  showAllOption: PropTypes.bool,
  /** 전체 선택 옵션의 라벨 */
  allOptionLabel: PropTypes.string,
};

/**
 * PopoverFilterButton 컴포넌트
 *
 * 팝오버 형태의 필터 버튼을 렌더링합니다.
 *
 * @param {string} className - 추가 CSS 클래스 이름
 * @param {string} placeholder - 선택 전 표시할 기본 텍스트
 * @param {string} label - 선택창 상단에 표시할 라벨
 * @param {Array<{label: string, value: any}>} options - 선택 옵션 배열 ({ label, value } 형식)
 * @param {Array<any>} selected - 선택된 값 배열
 * @param {boolean} multiple - 다중 선택 여부
 * @param {function} onChange - 선택값 변경 시 호출되는 콜백 함수
 * @param {boolean} showAllOption - 전체 선택 옵션 표시 여부
 * @param {string} allOptionLabel - 전체 선택 옵션의 라벨
 * @param {boolean} isGroup - 그룹 선택 여부
 * @param {Array<{label: string, options: Array<{label: string, value: any}>}>} groups - 그룹 선택 옵션 배열
 * @returns {JSX.Element} - 팝오버 필터 버트 컴포넌트
 */
const PopoverFilterButton = ({
  className = "",
  defaultSelectClassName = "",
  placeholder = "선택",
  label = "",
  options = [],
  selected = [],
  multiple = false,
  onChange = () => {},
  showAllOption = false,
  allOptionLabel = "전체",
  isGroup = false,
  groups = [],
  sections = [],
  onReset,
}) => {
  const {
    isOpen,
    setIsOpen,
    togglePopover,
    selectedValues,
    selectAll,
    handleSelect,
    handleSelectAll,
    popoverRef,
  } = useSelectHook({
    selected: selected,
    options,
    multiple,
    onChange,
  });

  // 🎯 스크롤 추적을 위한 ref들
  const triggerRef = useRef(null);
  const popoverContentRef = useRef(null);

  // 🎯 팝오버 스크롤 추적 훅 사용
  usePopoverScrollTracking(isOpen, triggerRef, popoverContentRef, true);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div ref={popoverRef} className={`${styles.withPopover} ${className}`}>
      <div ref={triggerRef}>
        <DefaultfilterButton
          isOpen={isOpen}
          isSelected={(() => {
            if (sections.length > 0) {
              // sections 모드: 어느 하나라도 selected가 있으면 true
              return sections.some((section) => {
                const sectionSelected = section.selected || [];
                return sectionSelected.length > 0;
              });
            } else {
              // 기존 모드: selectedValues로 판단
              return selectedValues != null && selectedValues.length > 0;
            }
          })()}
          placeholder={placeholder}
          onClick={togglePopover}
          className={defaultSelectClassName}
        />
      </div>
      {isOpen && (
        <div ref={popoverContentRef} className={`${styles.popoverFilterContainer}`}>
          <HorizontalFlex
            className={`${styles.header}`}
            leftChildren={
              <>
                <div className={`${styles.label}`}>{label}</div>
                {multiple && <div className={`${styles.isMultipleLabel}`}>중복선택가능</div>}
              </>
            }
            rightChildren={
              <div
                className={`${styles.close}`}
                onClick={() => {
                  setIsOpen(false);
                }}
              >
                <img src={icons.close.black} width={17} height={17} />
              </div>
            }
          />
          <div className={`${styles.body}`}>
            {sections.length > 0 ? (
              // sections가 있으면 섹션별로 렌더링
              sections.map((section, idx) => (
                <div key={idx} className={styles.groupSection}>
                  <div className={styles.groupLabel}>{section.label}</div>
                  <div className={styles.optionWrapper}>
                    {/* showAllOption이 true일 때만 '전체' 버튼 표시 */}
                    {section.showAllOption && (
                      <button
                        className={`${styles.chip} ${
                          (() => {
                            // 해당 섹션에 아무것도 선택되지 않았을 때만 '전체' 버튼이 selected 상태
                            const sectionSelected = section.selected || [];
                            return sectionSelected.length === 0;
                          })()
                            ? styles.selected
                            : ""
                        }`}
                        onClick={() => {
                          if (section.onChange) {
                            // 기존 필터들과 동일하게 '전체' 버튼 클릭 시 모든 선택 해제
                            section.onChange([]);
                          }
                        }}
                      >
                        전체
                      </button>
                    )}
                    {section.options.map((option, index) => {
                      const isSelected =
                        section.selected && section.selected.includes(option.value);
                      return (
                        <button
                          key={index}
                          className={`${styles.chip} ${isSelected ? styles.selected : ""}`}
                          onClick={() => {
                            if (section.onChange) {
                              let newSelected;
                              if (isSelected) {
                                // 이미 선택된 경우 제거
                                newSelected = section.selected.filter(
                                  (val) => val !== option.value
                                );
                              } else {
                                // 선택되지 않은 경우 추가
                                newSelected = [...(section.selected || []), option.value];
                                // useSelectHook과 동일한 로직: 전체 선택되었으면 초기화
                                if (newSelected.length === section.options.length) {
                                  newSelected = [];
                                }
                              }
                              section.onChange(newSelected);
                            }
                          }}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            ) : multiple ? (
              // multiple = true → 기존 칩 렌더링 유지
              <>
                {showAllOption && (
                  <button
                    className={`${styles.chip} ${selectAll ? styles.selected : ""}`}
                    onClick={() => {
                      if (!selectAll) {
                        handleSelectAll();
                      }
                    }}
                  >
                    {allOptionLabel}
                  </button>
                )}
                {isGroup
                  ? groups.map((group, gIdx) => (
                      <div key={gIdx} className={styles.groupSection}>
                        <div className={styles.groupLabel}>{group.label}</div>
                        <div className={styles.optionWrapper}>
                          {group.options.map((option, index) => (
                            <button
                              key={index}
                              className={`${styles.chip} ${selectedValues != null && selectedValues.includes(option.value) && !selectAll ? styles.selected : ""}`}
                              onClick={() => handleSelect(option.value)}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))
                  : options.map((option, index) => (
                      <button
                        key={index}
                        className={`${styles.chip} ${selectedValues != null && selectedValues.includes(option.value) && !selectAll ? styles.selected : ""}`}
                        onClick={() => handleSelect(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
              </>
            ) : (
              // multiple = false → 라디오(이미지) 형태로 두 개씩 정렬
              <>
                {showAllOption && (
                  <button
                    className={`${styles.chip} ${!selectedValues || selectedValues.length === 0 ? styles.selected : ""}`}
                    onClick={() => onChange("all")}
                  >
                    {allOptionLabel}
                  </button>
                )}
                {isGroup
                  ? groups.map((group, gIdx) => (
                      <div key={gIdx} className={styles.groupSection}>
                        <div className={styles.groupLabel}>{group.label}</div>
                        <div className={styles.optionWrapper}>
                          {group.options.map((option, index) => {
                            const isChecked =
                              selectedValues != null && selectedValues.includes(option.value);
                            return (
                              <button
                                key={index}
                                onClick={() => handleSelect(option.value)}
                                className={`${styles.chip} ${selectedValues != null && selectedValues.includes(option.value) && !selectAll ? styles.selected : ""}`}
                                style={{ display: "flex", alignItems: "center", gap: 8 }}
                              >
                                <img
                                  src={isChecked ? icons.radio.check : icons.radio.unCheck}
                                  alt={isChecked ? "checked" : "unchecked"}
                                />
                                <span className={styles.radioLabel}>{option.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  : options.map((option, index) => {
                      const isChecked =
                        selectedValues != null && selectedValues.includes(option.value);
                      return (
                        <button
                          key={index}
                          onClick={() => handleSelect(option.value)}
                          className={`${styles.chip} ${selectedValues != null && selectedValues.includes(option.value) && !selectAll ? styles.selected : ""}`}
                          style={{ display: "flex", alignItems: "center", gap: 8 }}
                        >
                          <img
                            src={isChecked ? icons.radio.check : icons.radio.unCheck}
                            alt={isChecked ? "checked" : "unchecked"}
                          />
                          <span className={styles.radioLabel}>{option.label}</span>
                        </button>
                      );
                    })}
              </>
            )}
            {onReset && <DefaultResetButton onClick={onReset} />}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * BottomSheetSelect 컴포넌트
 *
 * 바텀시트 형태의 선택 UI를 렌더링합니다.
 *
 * @param {string} className - 추가 CSS 클래스 이름
 * @param {string} placeholder - 선택 전 표시할 기본 텍스트
 * @param {string} label - 선택창 상단에 표시할 라벨
 * @param {Array<{label: string, value: any}>} options - 선택 옵션 배열
 * @param {Array<any>} selected - 선택된 값 배열
 * @param {boolean} multiple - 다중 선택 여부
 * @param {function} onChange - 선택값 변경 시 호출되는 콜백 함수
 * @param {boolean} showAllOption - 전체 선택 옵션 표시 여부
 * @param {string} allOptionLabel - 전체 선택 옵션의 라벨
 * @returns {JSX.Element} - 바텀시트 선택 컴포넌트
 */

const BottomSheetSelect = ({
  className = "",
  placeholder = "선택",
  label = "",
  options = [],
  selected = [],
  multiple = false,
  onChange = () => {},
  showAllOption = false,
  allOptionLabel = "전체",
}) => {
  const {
    isOpen,
    setIsOpen,
    togglePopover,
    selectedValues,
    selectAll,
    handleSelect,
    handleSelectAll,
    popoverRef,
  } = useSelectHook({
    selected: selected,
    options,
    multiple,
    onChange,
  });

  return (
    <div ref={popoverRef} className={className}>
      <DefaultSelectButton
        isSelected={selectedValues != null && selectedValues.length > 0}
        placeholder={placeholder}
        onClick={togglePopover}
      />

      <BottomSheet isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <VerticalFlex className={styles.bottomSheetContainer}>
          <HorizontalFlex
            className={styles.header}
            leftChildren={
              <>
                <div className={styles.label}>{label}</div>
                {multiple && <div className={styles.isMultipleLabel}>중복 선택 가능</div>}
              </>
            }
            rightChildren={
              <div className={styles.close} onClick={() => setIsOpen(false)}>
                <img src={icons.close.black} width={24} height={24} />
              </div>
            }
          />
          <div className={styles.body} expand={true}>
            <div>
              {multiple && showAllOption && (
                <button
                  className={`${styles.chip} ${selectAll ? styles.selected : ""}`}
                  onClick={() => {
                    if (!selectAll) {
                      handleSelectAll();
                    }
                  }}
                >
                  <div>{allOptionLabel}</div>
                  <div className={`${styles.iconContainer}`}>
                    {selectAll ? (
                      <img className={`${styles.checkIcon}`} src={icons.check.blue} />
                    ) : null}
                  </div>
                </button>
              )}

              {options.map((option, index) => (
                <button
                  key={index}
                  className={`${styles.chip} ${selectedValues != null && selectedValues.includes(option.value) && !selectAll ? styles.selected : ""}`}
                  onClick={() => handleSelect(option.value)}
                >
                  <div>{option.label}</div>
                  <div className={`${styles.iconContainer}`}>
                    {selectedValues != null &&
                    selectedValues.includes(option.value) &&
                    !selectAll ? (
                      <img className={`${styles.checkIcon}`} src={icons.check.blue} />
                    ) : null}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </VerticalFlex>
      </BottomSheet>
    </div>
  );
};

BottomSheetSelect.propTypes = {
  /** 추가 CSS 클래스 이름 */
  className: PropTypes.string,
  /** 선택 전 표시할 기본 텍스트 */
  placeholder: PropTypes.string,
  /** 선택창 상단에 표시할 라벨 */
  label: PropTypes.string,
  /** 선택 옵션 배열 ({ label, value } 형식) */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      /** 옵션의 표시 텍스트 */
      label: PropTypes.string.isRequired,
      /** 옵션의 값 */
      value: PropTypes.any.isRequired,
    })
  ),
  /** 선택된 값 배열 */
  selected: PropTypes.array,
  /** 다중 선택 여부 */
  multiple: PropTypes.bool,
  /** 선택값 변경 시 호출되는 콜백 함수 */
  onChange: PropTypes.func,
  /** 전체 선택 옵션 표시 여부 */
  showAllOption: PropTypes.bool,
  /** 전체 선택 옵션의 라벨 */
  allOptionLabel: PropTypes.string,
};

const RangeSection = ({ section, unit }) => {
  const middleRangeValue =
    typeof section?.rangeMaxLimit === "number" ? section.rangeMaxLimit / 2 : 0;

    // 인풋박스용 값 포맷팅 함수 (10,000만원 이상은 억 단위로 변환, 소수점 첫째 자리까지)
    const formatValueForInput = (val) => {
      if (val == null || val === '' || val === 0) return '';
      
      // 숫자로 변환 (쉼표 제거)
      const numValue = typeof val === 'string' ? Number(val.replace(/,/g, '')) : Number(val);
      if (isNaN(numValue)) return '';
      return numValue.toLocaleString();
    };

  // 인풋박스용 unit 계산 함수
  const getUnitForInput = (val) => {
    if (val == null || val === "" || val === 0) return "";

    const numValue = Number(val);
    if (isNaN(numValue)) return "";

    // 10,000만원 이상이면 "억원", 미만이면 "만원"
    if (numValue >= 10000) {
      return "억원";
    } else {
      return "만원";
    }
  };

  const formatUnit = (value) => {
    if (value == null || value === "" || isNaN(Number(value))) return "";

    const numValue = Number(value);

    if (numValue >= 10000) {
      // 억 단위
      const rounded = Math.floor(numValue / 10000);
      return `${rounded}억`;
    } else {
      // 만원 단위 (천 단위 이상 ,자동)
      return `${numValue.toLocaleString()}만원`;
    }
  };

    const [range, setRange] = useState(() => {
        const min = section.min ?? 0;
        const rawMax = section.max;
        const max =
          rawMax == null || rawMax > section.rangeMaxLimit
            ? section.rangeMaxLimit
            : rawMax;
        // 값이 범위 내에 있도록 제한하고 정렬 보장
        const clampedMin = Math.min(Math.max(min, 0), section.rangeMaxLimit);
        const clampedMax = Math.min(Math.max(max, 0), section.rangeMaxLimit);
        const sortedMin = Math.min(clampedMin, clampedMax);
        const sortedMax = Math.max(clampedMin, clampedMax);
        return [sortedMin, sortedMax];
      });
    const [minUnit, setMinUnit] = useState(unit);
    const [maxUnit, setMaxUnit] = useState(unit);
 
    useEffect(() => {
        const min = section.min ?? 0;
        const rawMax = section.max;
      
        const isMinValid = min <= section.rangeMaxLimit;
        const isMaxValid = rawMax != null && rawMax <= section.rangeMaxLimit;
      
        const isMinOverLimit = min > section.rangeMaxLimit;
        const isMaxOverLimit = rawMax != null && rawMax > section.rangeMaxLimit;
        const isMinGreaterThanMax = rawMax != null && min > rawMax;
      
        // 범위를 초과하거나 정렬되지 않은 경우 슬라이더를 안전한 값으로 리셋
        if (isMinOverLimit || isMaxOverLimit || isMinGreaterThanMax) {
          // 슬라이더는 항상 범위 내의 값으로 설정
          const safeMin = isMinValid ? Math.min(min, section.rangeMaxLimit) : 0;
          const safeMax = isMaxValid ? Math.min(rawMax, section.rangeMaxLimit) : section.rangeMaxLimit;        // 값이 정렬되도록 보장
          const sortedMin = Math.min(safeMin, safeMax);
          const sortedMax = Math.max(safeMin, safeMax);
          setRange([sortedMin, sortedMax]);
          return;
        }
      
        // 정상적인 경우: 범위 내의 값들
        const max = rawMax ?? section.rangeMaxLimit;
        // 값이 범위 내에 있도록 제한하고 정렬 보장
        const clampedMin = Math.min(Math.max(min, 0), section.rangeMaxLimit);
        const clampedMax = Math.min(Math.max(max, 0), section.rangeMaxLimit);
        const sortedMin = Math.min(clampedMin, clampedMax);
        const sortedMax = Math.max(clampedMin, clampedMax);
        setRange([sortedMin, sortedMax]);
      }, [section.min, section.max, section.rangeMaxLimit]);  

    const debouncedUpdate = useMemo(() => debounce(([min, max]) => {
      section.onMinChange(min);
      section.onMaxChange(max);
    }, 300), [section]);
  
    const handleMinChange = (val) => {
        // val은 이미 MinMaxInput의 handleBlur에서 unit 변환이 완료된 만원 단위 값입니다
        // 빈 값 처리
        if (val === '' || val == null) {
          setRange([0, range[1]]);
          debouncedUpdate([null, section.max]);
          return;
        }
        
        const parsed = Number(val);
        const isEmpty = parsed === 0;
      
        if (isEmpty) {
          // 최소값이 없으면 0으로 처리, 실제 전달은 null
          setRange([0, range[1]]);
          debouncedUpdate([null, section.max]);
          return;
        }
      
        if (parsed > section.rangeMaxLimit) {
          // 입력값이 범위를 초과할 경우:
          // 슬라이더는 리셋, 값은 그대로 전달
          setRange([0, section.rangeMaxLimit]);
          debouncedUpdate([parsed, section.max]);
          return;
        }

        const newMin = Math.min(parsed, range[1]);
        setRange([newMin, range[1]]);
        debouncedUpdate([parsed, section.max]);
      };
    
    const handleMaxChange = (val) => {
        // val은 이미 MinMaxInput의 handleBlur에서 unit 변환이 완료된 만원 단위 값입니다
        // 빈 값 처리
        if (val === '' || val == null) {
          setRange([range[0], section.rangeMaxLimit]);
          debouncedUpdate([range[0], null]);
          return;
        }
        
        const parsed = Number(val);
        const isEmpty = parsed === 0;
      
        if (isEmpty) {
          setRange([range[0], section.rangeMaxLimit]);
          debouncedUpdate([range[0], null]);
          return;
        }
      
        if (parsed > section.rangeMaxLimit) {
          // 슬라이더 리셋, 값은 그대로 보존
          setRange([0, section.rangeMaxLimit]);
          debouncedUpdate([range[0], parsed]); // 실제로 payload엔 초과 값 전달
          return;
        }
      
        const cappedMax = Math.min(parsed, section.rangeMaxLimit);
        setRange([range[0], cappedMax]);
        debouncedUpdate([range[0], parsed]);
      };
  
    return (
      <div className={styles.priceSection}>
        {section.hasTitle && <div className={styles.rangeTitle}>{section.title}</div>}
        <HorizontalFlex>
          <MinMaxInput
            type="min"
            counterpartValue={section.maxLimit}
            value={
                section.min > section.rangeMaxLimit
                  ? formatValueForInput(section.min)
                  : range[0] === 0
                    ? ''
                    : formatValueForInput(range[0])
              }
            onChange={handleMinChange}
            unit={
                minUnit
              }
          />
          
          <span style={{ padding: "0 10px" }}>~</span>
          <MinMaxInput
            type="max"
            counterpartValue={range[0]}
            value={
                section.max > section.rangeMaxLimit
                  ? formatValueForInput(section.max)
                  : range[1] === section.rangeMaxLimit
                    ? ''
                    : formatValueForInput(range[1])
              }
            onChange={handleMaxChange}
            unit={
                maxUnit
              }
          />
        </HorizontalFlex>
        <Range
          step={section.step}
          min={0}
          max={section.rangeMaxLimit}
          values={range}
          onChange={([newMin, newMax]) => {
            setRange([newMin, newMax])}}
          onFinalChange={([newMin, newMax]) => {
            section.onMinChange(newMin === 0 ? null : newMin);
            section.onMaxChange(newMax === section.rangeMaxLimit ? null : newMax);
          }}
          renderTrack={({ props, children }) => (
                        <div
                          {...props}
                          className={styles.track}
                          style={{
                            ...props.style,
                            height: "6px",
                            background: getTrackBackground({
                                values: range,
                                colors: ['#ddd', '#0080ff', '#ddd'],
                                min: 0,
                                max: section.rangeMaxLimit
                              }),
                            marginTop: "20px",
                          }}
                        >
                          <div className ={styles.sliderTicks}>
                            <div className={styles.tick}>
                              {/* <div className={styles.line} /> */}
                              <div className={styles.label}>최소</div>
                            </div>
                            <div className={styles.tick}>
                              {/* <div className={styles.line} /> */}
                              <div className={styles.label}>{formatUnit(middleRangeValue)}</div>


                            </div>
                            <div className={styles.tick}>
                              {/* <div className={styles.line} /> */}
                              <div className={styles.label}>최대</div>
                            </div>
                          </div>
                          {children}
                        </div>
                      )}
            renderThumb={({ props, index }) => {
                          const { key, ...rest } = props;
                          const value = range[index];
                          const limitedValue = Math.min(value, section.rangeMaxLimit);
                      

          return (
            <div
              key={key}
              {...rest}
              className={styles.thumb}
              style={{
                position: "absolute",
                zIndex: "2",
                height: "20px",
                width: "20px",
                backgroundColor: "rgb(0, 128, 255)",
                borderRadius: "50%",
                fill: "#FFF",
                strokeWidth: "1px",
                stroke: "#EAEAEA",
                filter: "drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.15))",
                ...rest.style,
              }}
            />
          );
        }}
      />
    </div>
  );
};

const RangeSelect = ({
  className = "",
  label = "가격",
  placeholder = "",
  unit = "평",
  changeUnit = "m²",
  showUnitToggle = false,
  sections = [],
  onUnitChange = () => {},
  onReset = null,
}) => {
  const [selectedUnit, setSelectedUnit] = useState(unit);

  const handleUnitToggle = (newUnit) => {
    if (newUnit === selectedUnit) return;

    // 단위 변환
    const convert = (value, from, to) => {
      if (value == null) return null;
      if (from === "평" && to === "m²") return Math.round(value * 3.3058);
      if (from === "m²" && to === "평") return Math.round(value * 0.3025);
      return value;
    };

    sections.forEach((section) => {
      if (section.min != null && section.onMinChange) {
        section.onMinChange(convert(section.min, selectedUnit, newUnit));
      }
      if (section.max != null && section.onMaxChange) {
        section.onMaxChange(convert(section.max, selectedUnit, newUnit));
      }
    });

    setSelectedUnit(newUnit);
    onUnitChange(newUnit); // 외부에서도 단위 업데이트 가능
  };

  const isSectionSelected = (sections) => {
    return sections.some((section) => section.min !== null || section.max !== null);
  };
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef(null);
  const togglePopover = () => setIsOpen((prev) => !prev);

  // 🎯 스크롤 추적을 위한 ref들
  const triggerRef = useRef(null);
  const popoverContentRef = useRef(null);

  // 🎯 팝오버 스크롤 추적 훅 사용
  usePopoverScrollTracking(isOpen, triggerRef, popoverContentRef, true);

  // 외부 클릭 시 팝오버 닫기
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div ref={popoverRef} className={`${styles.withPopover} ${className}`}>
      <div ref={triggerRef}>
        <DefaultfilterButton
          isOpen={isOpen}
          isSelected={isSectionSelected(sections)}
          placeholder={placeholder}
          onClick={togglePopover}
        />
      </div>
      {isOpen && (
        <div
          ref={popoverContentRef}
          className={`${styles.popoverFilterContainer} ${styles.popover}`}
        >
          <HorizontalFlex
            className={`${styles.header}`}
            leftChildren={
              <>
                <div className={`${styles.label}`}>{label}</div>
                {showUnitToggle && (
                  <div className={styles.unitToggleWrapper}>
                    <button
                      className={`${styles.unitButton} ${selectedUnit === unit ? styles.active : ""}`}
                      onClick={() => handleUnitToggle(unit)}
                    >
                      평
                    </button>
                    <button
                      className={`${styles.unitButton} ${selectedUnit === changeUnit ? styles.active : ""}`}
                      onClick={() => handleUnitToggle(changeUnit)}
                    >
                      m²
                    </button>
                  </div>
                )}
              </>
            }
            rightChildren={
              <div className={`${styles.close}`} onClick={() => setIsOpen(false)}>
                <img src={icons.close.black} width={17} height={17} />
              </div>
            }
          />
          <div className={`${styles.body}`} style={{ paddingTop: "0px" }}>
            <VerticalFlex>
              {sections.map((section, idx) => (
                <React.Fragment key={idx}>
                  <RangeSection section={section} unit={selectedUnit} />
                  {idx < sections.length - 1 && <div className={styles.sectionDivider} />}
                </React.Fragment>
              ))}
              {onReset && <DefaultResetButton onClick={onReset} size={18} />}
            </VerticalFlex>
          </div>
        </div>
      )}
    </div>
  );
};

const AllSelect = ({
  children,
  disabled = false,
  highlighted = false,
  src = "",
  blueSrc = "",
  isIcon = false,
  placeholder,
  width = "40px",
  height = "40px",
  label = "",
  className,
  unitIndex,
  unitOptions,
  onToggleUnit,
  isSelected,
  onReset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef(null);
  const togglePopover = () => setIsOpen((prev) => !prev);
  const activeUnit = unitOptions?.[unitIndex];

  // 스크롤 추적을 위한 ref들
  const triggerRef = useRef(null);
  const popoverContentRef = useRef(null);

  // 팝오버 스크롤 추적 훅 사용
  usePopoverScrollTracking(isOpen, triggerRef, popoverContentRef, true);
  // 외부 클릭 시 팝오버 닫기
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div
      ref={popoverRef}
      onMouseDown={isIcon ? togglePopover : undefined}
      className={
        isIcon
          ? `
                    
                    ${buttonStyle.iconButton}
                    ${buttonStyle.button}         
                    ${className}
                `
          : `${styles.withPopover} ${className}`
      }
      style={isIcon ? { height: width, width: height } : undefined}
    >
      <div ref={triggerRef}>
        {isIcon ? (
          src && (
            <div
              style={
                isSelected
                  ? { backgroundImage: `url(${blueSrc})` }
                  : { backgroundImage: `url(${src})` }
              }
              className={`${buttonStyle.image} `}
            />
          )
        ) : (
          <DefaultfilterButton
            isOpen={isOpen}
            placeholder={placeholder}
            onClick={togglePopover}
            isSelected={isSelected}
          />
        )}
      </div>

      {isOpen && (
        <div
          ref={popoverContentRef}
          className={`${styles.popoverFilterContainer} ${styles.popover}`}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <HorizontalFlex
            className={`${styles.header}`}
            leftChildren={
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div className={`${styles.label}`}>{label}</div>
                  <div className={styles.unitToggleWrapper}>
                    {unitOptions &&
                      unitOptions.length > 0 &&
                      unitOptions.map((unit, idx) => (
                        <button
                          key={unit.label}
                          className={`${styles.unitButton} ${unitIndex === idx ? styles.active : ""}`}
                          onClick={(e) => {
                            e.stopPropagation(); // 팝오버 닫힘 방지
                            if (unitIndex === idx) return;
                            onToggleUnit(idx); // 특정 인덱스로 설정
                          }}
                        >
                          {unit.label}
                        </button>
                      ))}
                  </div>
                </div>
              </>
            }
            rightChildren={
              <div
                className={`${styles.close}`}
                onClick={() => {
                  setIsOpen(false);
                }}
              >
                <img src={icons.close.black} width={17} height={17} />
              </div>
            }
          />
          <div className={`${styles.body}`}>
            <VerticalFlex>{children}</VerticalFlex>
            {onReset && <DefaultResetButton onClick={onReset} />}
          </div>
        </div>
      )}
    </div>
  );
};

const SelectOrRange = ({
  type,
  multiple = true,
  showAllOption = true,
  allOptionLabel = "전체",
  options = [],
  sections = [],
  selected,
  selectTitle = "",
  onChange = () => {},
  unit = "",
}) => {
  const {
    isOpen,
    setIsOpen,
    togglePopover,
    selectedValues,
    selectAll,
    handleSelect,
    handleSelectAll,
  } = useSelectHook({
    selected: selected,
    options,
    multiple,
    onChange,
  });

  if (type === "select") {
    return (
      <>
        <div className={`${styles.body}`} style={{ padding: "0px", width: "100%" }}>
          <div className={styles.rangeTitle} style={{ margin: "0px", paddingTop: "0px" }}>
            {selectTitle}
          </div>
          {multiple && showAllOption && (
            <button
              className={`${styles.chip} ${selectAll ? styles.selected : ""}`}
              onClick={() => {
                if (!selectAll) {
                  handleSelectAll();
                }
              }}
            >
              {allOptionLabel}
            </button>
          )}

          {options.map((option, index) => (
            <button
              key={index}
              className={`${styles.chip} ${selectedValues != null && selectedValues.includes(option.value) && !selectAll ? styles.selected : ""}`}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </>
    );
  }
  if (type === "range")
    return (
      <>
        {sections.map((section, idx) => (
          <RangeSection key={idx} section={section} unit={unit} />
        ))}
      </>
    );

  return;
};

const CustomSelectPopup = ({}) => {
  const { isOpen, setIsOpen, togglePopover } = useSelectHook({});

  return (
    <>
      <div className={`${styles.body}`} style={{ padding: "0px", width: "100%" }}>
        <div className={styles.rangeTitle} style={{ margin: "0px", paddingTop: "0px" }}>
          {selectTitle}
        </div>
        {options.map((option, index) => (
          <button
            key={index}
            className={`${styles.chip} ${selectedValues != null && selectedValues.includes(option.value) && !selectAll ? styles.selected : ""}`}
            onClick={() => handleSelect(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </>
  );
};

export {
  DropdownSelect,
  PopoverChipSelect,
  BottomSheetSelect,
  RangeSelect,
  AllSelect,
  RangeSection,
  SelectOrRange,
  PopoverFilterButton,
};
